import { useEffect } from "react";
import {
  AD_ACCOUNT_PLATFORMS_LABELS,
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE,
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION,
  ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  BORDER_RADIUS,
  FONT_FAMILY,
  GOOGLE,
  GOOGLE_APP_STORE,
  APPLE_APP_STORE,
  googleCurrenciesWithDecimals,
} from "../../../../utils/constants";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  optionsAdsCampaignTypeBudgets,
  optionsCampaignsTypeGoogleAds,
} from "../../../../utils/options";
import {
  FIELD_REQUIRED,
  CAMPAIGN_TYPE_PLACEHOLDER,
  NETWORK_DISPLAY_SETTING_PLACEHOLDER,
  GOOGLE_NETWORK_SETTING_GOOGLE_SEARCH,
  GOOGLE_NETWORK_SETTING_SEARCH_NETWORK,
  GOOGLE_NETWORK_SETTING_CONTENT_NETWORK,
  CONVERSION_ACTIONS_PLACEHOLDER,
  ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_SUB_TITLE,
  TYPE_BUDGET_PLACEHOLDER,
  LIFETIME_BUDGET_PLACEHOLDER,
  DAILY_BUDGET_PLACEHOLDER,
  START_DATE_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  ALERT_CHANGE_AD_ACCOUNT_GENERAL_TITLE,
  BUTTON_CONTINUE,
  BUTTON_CANCEL,
  ALERT_CHANGE_AD_ACCOUNT_GOOGLE_DESCRIPTION,
  ADS_CAMPAIGN_PLATFORM_ADDITIONAL_CONFIGURATION_SUB_TITLE,
  MERCHANT_CENTER_ID_PLACEHOLDER,
  ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_GENERAL,
  LOCATIONS_PLACEHOLDER,
  AD_SCHEDULE_PLACEHOLDER,
  LANGUAGES_PLACEHOLDER,
  APP_STORE_PLACEHOLDER,
  APP_STORE_ANDROID,
  APP_STORE_IOS,
  APP_PLACEHOLDER,
  GOOGLE_DSA_SETTINGS_TITLE,
  GOOGLE_DSA_SETTINGS_EXPLANATION,
  GOOGLE_DSA_DOMAIN_LABEL,
  GOOGLE_DSA_LANGUAGE_LABEL,
  GOOGLE_AI_MAX_ENABLE_QUESTION,
  GOOGLE_AI_MAX_ACTIVATED,
  GOOGLE_AI_MAX_DEACTIVATED,
} from "../../../../i18n/keysTranslations";
import Select from "../../../Form/Select";
import SelectBusinessAccount from "../../../Meta/SelectBusinessAccount";
import CheckBoxes from "../../../Form/CheckBoxes";
import SelectConversionActionsGoogle from "../../../Google/SelectConversionActionsGoogle";
import { addDays } from "date-fns";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../../../lib/i18next";
import SearchSegmentationsField from "../../../Form/SearchSegmentationsField";
import TextField from "../../../Form/TextField";
import { useDispatchApp } from "../../../../lib/redux";
import ReasoningContainer from "../../../../pages/NewAdsCampaign/components/Common/ReasoningContainer";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import NumberField from "../../../Form/NumberField";
import { getCurrencyAdAccountPlatform } from "../../../../actions/getters";
import DateField from "../../../Form/DateField";
import startOfDay from "date-fns/startOfDay";
import { ConfirmAlert } from "../../../Alerts/Alerts";
import CampaignGoogleSearchForm from "./CampaignGoogleSearchForm";
import SiteLinksContainer from "./SiteLinksContainer";
import CalloutsContainer from "./CalloutsContainer";
import CustomAudiencesContainerGoogle from "../Common/CustomAudiencesContainerGoogle";
import SelectMerchantGoogle from "./SelectMerchantGoogle";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import { Box, Divider } from "@mui/material";
import WeeklyTimeSelector from "../../../../pages/NewAdsCampaign/components/Common/WeeklyTimeSelector";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BiddingStrategyGoogleForm from "./BiddingStrategyGoogleForm";
import CardContainer from "../../../Card/CardContainer";
import SelectLanguagesGoogleForm from "./SelectLanguagesGoogleForm";
import { LANGUAGES_GOOGLE_DSA_OPTIONS } from "../../../../utils/catalogs/languageOptions";
import ChipOptional from "../../../Chip/ChipOptional";
import SelectGoogleApp from "./SelectGoogleApp";
import { getReactHookFormNestedError } from "../../../../utils/errors";

const platform = GOOGLE;
const grid = {
  xs: 12,
  lg: 6,
};

const CampaignGoogleForm = () => {
  const {
    control,
    formState,
    watch,
    setValue,
    getValues,
    isGeneralDisabledFields,
    getIsCampaignPlatformCreated,
  } = useFormContext();
  useFocusErrorForm(formState);
  const errors = formState.errors;

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const basicPath = `platformsData.${platform}`;
  const platformData = watch(basicPath);
  const isIncompatible = platformData?.isIncompatible;
  const objective = platformData?.objective;
  const typeBudget = platformData?.typeBudget;
  const startDate = platformData?.startDate;
  const endDate = platformData?.endDate;
  const language = watch("language");
  const appStore = platformData?.appStore;
  const adsGroups = watch("adsGroups") || [];
  const hasDsaAdGroups =
    objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION &&
    adsGroups.some(
      (ag) =>
        ag.platform === GOOGLE &&
        ag.type === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION
    );

  const errorAppStore = getReactHookFormNestedError(
    errors,
    `${basicPath}.appStore`
  );
  const isCampaignPlatformCreated = getIsCampaignPlatformCreated(platform);

  const isDisabledFields = isIncompatible || isGeneralDisabledFields;

  useEffect(() => {
    if (
      objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION &&
      platformData?.enableAIMax === undefined
    ) {
      setValue(`${basicPath}.enableAIMax`, false, { shouldDirty: false });
    }
  }, [objective, platformData?.enableAIMax, basicPath, setValue]);

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform,
      integrationID: platformData?.integrationID,
      adAccountID: platformData?.adAccountID,
    })
  );

  const validationChangeAdAccount = async () => {
    const result = await ConfirmAlert({
      title: t(ALERT_CHANGE_AD_ACCOUNT_GENERAL_TITLE),
      html: (
        <Typography
          variant="body1"
          sx={{
            fontFamily: FONT_FAMILY,
            "& *": {
              p: "revert",
              m: "revert",
              listStyle: "revert",
              textDecoration: "revert",
            },
          }}
          dangerouslySetInnerHTML={{
            __html: t(ALERT_CHANGE_AD_ACCOUNT_GOOGLE_DESCRIPTION),
          }}
        />
      ),
      confirmButtonText: t(BUTTON_CONTINUE),
      cancelButtonText: t(BUTTON_CANCEL),
    });

    if (!result.isConfirmed) return false;

    return true;
  };

  const onChangeAdAccount = (value) => {
    setValue(`${basicPath}.integrationID`, value?.integrationID);
    setValue(`${basicPath}.conversionActions`, []);
    setValue(`${basicPath}.customAudiences`, []);
    setValue(`${basicPath}.budget`, "");
    const adsGroups = getValues("adsGroups");
    adsGroups.forEach((adGroup, index) => {
      if (adGroup.platform !== GOOGLE) return;
      setValue(`adsGroups.[${index}].customAudiences`, []);
    });
    const assetsGroups = getValues("assetsGroups");
    assetsGroups.forEach((assetGroup, index) => {
      if (assetGroup.platform !== GOOGLE) return;
      setValue(`assetsGroups.[${index}].customAudiences`, []);
    });
  };

  return (
    <Grid container spacing={2}>
      {!isCampaignPlatformCreated && (
        <Grid item xs={12}>
          <ReasoningContainer platform={platform} />
        </Grid>
      )}

      {/* CARD GENERAL CONFIGURATION */}
      <Grid item xs={12}>
        <CardContainer
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_GENERAL)}
              </Typography>
            </Box>
          }
        >
          <Grid container spacing={3}>
            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(CAMPAIGN_TYPE_PLACEHOLDER)}
              </Typography>
              <Select
                options={optionsCampaignsTypeGoogleAds}
                disabled={true}
                name={`${basicPath}.objective`}
                variant="filled"
                fullWidth
                doTranslate={true}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Grid>

            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(AD_ACCOUNT_PLATFORMS_LABELS[platform])}
              </Typography>
              <SelectBusinessAccount
                variant="filled"
                disabled={isCampaignPlatformCreated || isDisabledFields}
                control={control}
                platform={platform}
                name={`${basicPath}.adAccountID`}
                onChangeAction={onChangeAdAccount}
                validationChange={validationChangeAdAccount}
                errors={errors}
                fullWidth={true}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Grid>

            {objective === ADS_CAMPAIGN_TYPE_APP_OPTION && (
              <>
                <Grid item {...grid}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t(APP_STORE_PLACEHOLDER)}
                  </Typography>
                  <FormControl
                    component="fieldset"
                    fullWidth
                    disabled={isCampaignPlatformCreated || isDisabledFields}
                  >
                    <RadioGroup
                      row
                      value={appStore || ""}
                      onChange={(e) => {
                        setValue(`${basicPath}.appStore`, e.target.value);
                        setValue(`${basicPath}.appID`, "");
                      }}
                    >
                      <FormControlLabel
                        value={GOOGLE_APP_STORE}
                        control={<Radio />}
                        label={t(APP_STORE_ANDROID)}
                        disabled={isCampaignPlatformCreated || isDisabledFields}
                      />
                      <FormControlLabel
                        value={APPLE_APP_STORE}
                        control={<Radio />}
                        label={t(APP_STORE_IOS)}
                        disabled={isCampaignPlatformCreated || isDisabledFields}
                      />
                    </RadioGroup>
                  </FormControl>
                  {errorAppStore && (
                    <Typography variant="caption" color="error">
                      {errorAppStore.message}
                    </Typography>
                  )}
                </Grid>
                <Grid item {...grid}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t(APP_PLACEHOLDER)}
                  </Typography>
                  <SelectGoogleApp
                    control={control}
                    errors={errors}
                    name={`${basicPath}.appID`}
                    store={appStore}
                    disabled={isCampaignPlatformCreated || isDisabledFields}
                    fullWidth
                    variant="filled"
                    placeholder={t(APP_PLACEHOLDER)}
                  />
                </Grid>
              </>
            )}

            {objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION && (
              <Grid item {...grid}>
                <CheckBoxes
                  name={`${basicPath}.networkSettings`}
                  disabled={isDisabledFields}
                  label={NETWORK_DISPLAY_SETTING_PLACEHOLDER}
                  options={[
                    {
                      key: "targetGoogleSearch",
                      label: GOOGLE_NETWORK_SETTING_GOOGLE_SEARCH,
                      disabled: true,
                    },
                    {
                      key: "targetSearchNetwork",
                      label: GOOGLE_NETWORK_SETTING_SEARCH_NETWORK,
                    },
                    {
                      key: "targetContentNetwork",
                      label: GOOGLE_NETWORK_SETTING_CONTENT_NETWORK,
                    },
                  ]}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )}

            {objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION && (
              <Grid item {...grid}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t(GOOGLE_AI_MAX_ENABLE_QUESTION)}
                </Typography>
                <Select
                  name={`${basicPath}.enableAIMax`}
                  disabled={isDisabledFields}
                  options={[
                    {
                      value: false,
                      label: GOOGLE_AI_MAX_DEACTIVATED,
                    },
                    {
                      value: true,
                      label: GOOGLE_AI_MAX_ACTIVATED,
                    },
                  ]}
                  control={control}
                  errors={errors}
                  variant="filled"
                  fullWidth
                  doTranslate={true}
                  useOnChange={false}
                  value={false}
                  actionsChange={(v) => {
                    setValue(
                      `${basicPath}.enableAIMax`,
                      v === true || v === "true"
                    );
                  }}
                />
              </Grid>
            )}

            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(CONVERSION_ACTIONS_PLACEHOLDER)}
              </Typography>
              <SelectConversionActionsGoogle
                disabled={isCampaignPlatformCreated || isDisabledFields}
                name={`${basicPath}.conversionActions`}
                integrationID={platformData?.integrationID}
                adAccountID={platformData?.adAccountID}
                appStore={platformData?.appStore}
                appID={platformData?.appID}
                objective={objective}
                variant="filled"
                fullWidth
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: false,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Grid>

            {objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION && (
              <Grid item {...grid}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t(MERCHANT_CENTER_ID_PLACEHOLDER)}
                </Typography>
                <SelectMerchantGoogle
                  name={`${basicPath}.merchantCenterID`}
                  disabled={isCampaignPlatformCreated || isDisabledFields}
                  control={control}
                  variant="filled"
                  fullWidth
                  integrationID={platformData?.integrationID}
                  adAccountID={platformData?.adAccountID}
                  errors={errors}
                />
              </Grid>
            )}

            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(START_DATE_PLACEHOLDER)}
              </Typography>
              <DateField
                sx={{
                  backgroundColor: "fields.background",
                  boxShadow: BOX_SHADOW_FIELDS,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  borderRadius: BORDER_RADIUS,
                }}
                control={control}
                name={`${basicPath}.startDate`}
                minDate={startOfDay(new Date())}
                actionsChange={(value) => {
                  setValue(
                    `${basicPath}.startDate`,
                    value ? startOfDay(value).toISOString() : null
                  );
                }}
                fullWidth
                variant="filled"
                errors={errors}
                disabled={isCampaignPlatformCreated || isDisabledFields}
                showStartNowButton={true}
              />
            </Grid>

            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(END_DATE_PLACEHOLDER)}
              </Typography>
              <DateField
                sx={{
                  backgroundColor: "fields.background",
                  boxShadow: BOX_SHADOW_FIELDS,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  borderRadius: BORDER_RADIUS,
                }}
                control={control}
                name={`${basicPath}.endDate`}
                value={endDate || null}
                minDate={addDays(
                  startDate ? new Date(startDate) : new Date(),
                  1
                )}
                actionsChange={(value) => {
                  setValue(
                    `${basicPath}.endDate`,
                    value ? startOfDay(value).toISOString() : null
                  );
                }}
                clearable={true}
                fullWidth
                variant="filled"
                errors={errors}
                disabled={isDisabledFields}
              />
            </Grid>

            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(LOCATIONS_PLACEHOLDER)}
              </Typography>
              <SearchSegmentationsField
                name={`${basicPath}.locations`}
                mode="fetching"
                platform={platform}
                type={ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE}
                adAccountID={platformData?.adAccountID}
                integrationID={platformData?.integrationID}
                objective={objective}
                language={language}
                fullWidth
                variant="filled"
                disabled={isDisabledFields}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Grid>

            <Grid item {...grid}>
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="body1">
                  {t(LANGUAGES_PLACEHOLDER)}
                </Typography>
                <ChipOptional size="small" />
              </Box>
              <SelectLanguagesGoogleForm
                name={`${basicPath}.languages`}
                control={control}
                disabled={isDisabledFields}
                errors={errors}
              />
            </Grid>
          </Grid>
        </CardContainer>
      </Grid>

      {/* CARD CUSTOM AUDIENCES */}
      {objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION && (
        <Grid item xs={12}>
          <CustomAudiencesContainerGoogle
            name={`${basicPath}.customAudiences`}
            control={control}
            disabled={isDisabledFields}
            from="campaign"
          />
        </Grid>
      )}

      {/* CARD BUDGET */}
      <Grid item xs={12}>
        <CardContainer
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(ADS_CAMPAIGN_AD_GROUP_BUDGET_INFORMATION_SUB_TITLE)}
              </Typography>
            </Box>
          }
        >
          <Grid container spacing={3}>
            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(TYPE_BUDGET_PLACEHOLDER)}
              </Typography>
              <Select
                disabled={true}
                name={`${basicPath}.typeBudget`}
                actionsChange={(value) => {
                  if (value === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION) {
                    setValue(`${basicPath}.startDate`, null);
                    setValue(`${basicPath}.endDate`, null);
                  } else {
                    setValue(
                      `${basicPath}.startDate`,
                      new Date().toISOString()
                    );
                    setValue(
                      `${basicPath}.endDate`,
                      addDays(new Date(), 7).toISOString()
                    );
                  }
                }}
                fullWidth
                variant="filled"
                doTranslate={true}
                options={optionsAdsCampaignTypeBudgets}
                control={control}
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                }}
              />
            </Grid>
            <Grid item {...grid}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t(
                  typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
                    ? DAILY_BUDGET_PLACEHOLDER
                    : LIFETIME_BUDGET_PLACEHOLDER,
                  {
                    currency: currency.toUpperCase(),
                  }
                )}
              </Typography>
              <TextField
                name={`${basicPath}.budget`}
                fullWidth
                variant="filled"
                InputProps={{
                  inputComponent: googleCurrenciesWithDecimals.includes(
                    currency
                  )
                    ? NumberDecimalField
                    : NumberField,
                }}
                control={control}
                errors={errors}
                disabled={isDisabledFields}
              />
            </Grid>
            <Grid item xs={12}>
              <BiddingStrategyGoogleForm
                name={`${basicPath}.biddingStrategy`}
                integrationID={platformData?.integrationID}
                adAccountID={platformData?.adAccountID}
                objective={objective}
                control={control}
                disabled={isDisabledFields}
                errors={errors}
              />
            </Grid>
          </Grid>
        </CardContainer>
      </Grid>

      {objective !== ADS_CAMPAIGN_TYPE_APP_OPTION && (
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon
                  sx={{ color: "#FFFFFF", width: 24, height: 24 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(AD_SCHEDULE_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <WeeklyTimeSelector
              name={`${basicPath}.schedule`}
              control={control}
              platform={platform}
              disabled={isDisabledFields}
            />
          </CardContainer>
        </Grid>
      )}

      {/* CARD DYNAMIC SEARCH ADS SETTINGS */}
      {hasDsaAdGroups && (
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(GOOGLE_DSA_SETTINGS_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(GOOGLE_DSA_SETTINGS_EXPLANATION)}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t(GOOGLE_DSA_DOMAIN_LABEL)}
                </Typography>
                <TextField
                  name={`${basicPath}.dynamicSearchAdsSetting.domain`}
                  variant="filled"
                  fullWidth
                  disabled={isDisabledFields}
                  control={control}
                  errors={errors}
                  rules={{
                    required: {
                      value: false,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t(GOOGLE_DSA_LANGUAGE_LABEL)}
                </Typography>
                <SelectLanguagesGoogleForm
                  name={`${basicPath}.dynamicSearchAdsSetting.language`}
                  control={control}
                  disabled={isDisabledFields}
                  errors={errors}
                  multiple={false}
                  options={LANGUAGES_GOOGLE_DSA_OPTIONS}
                />
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>
      )}

      {/* CARD BRAND CONFIGURATION */}
      {objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION && (
        <CampaignGoogleSearchForm platform={platform} grid={grid} />
      )}

      {/* CARD ADDITIONAL CONFIGURATION */}
      {objective !== ADS_CAMPAIGN_TYPE_APP_OPTION && (
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_PLATFORM_ADDITIONAL_CONFIGURATION_SUB_TITLE)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SiteLinksContainer
                  control={control}
                  errors={errors}
                  name={`${basicPath}.siteLinks`}
                  disabled={isDisabledFields}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <CalloutsContainer
                  control={control}
                  errors={errors}
                  name={`${basicPath}.callOuts`}
                  disabled={isDisabledFields}
                />
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>
      )}
    </Grid>
  );
};

export default CampaignGoogleForm;
